import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { NodeToolbar as NodeToolbarRaw, Position, useReactFlow } from '@xyflow/react';
import { useI18n } from '@/providers/i18n';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Sparkles, ScanSearch, Loader2Icon, Eraser, Image as ImageIcon, Eye, Gauge, Languages, CaptionsOff, Clapperboard } from 'lucide-react';
import { handleError } from '@/lib/error/handle';
import { cn } from '@/lib/utils';
import { uploadFile } from '@/lib/upload';
import { useAuth, useUser } from '@clerk/nextjs';
import { useProject } from '@/providers/project';
import { toast } from 'sonner';
import { videoModels, getVideoModelIdsByTag, VIDEO_MODEL_TAGS } from '@flowmuse/models/video';
import { getUiOptionsForModel } from '@flowmuse/models/video/ui-options';
import { useModelLabelById } from '@/lib/models/use-model-label';
import { useExtractFrame } from '@/hooks/use-extract-frame';
import { Slider } from '@/components/ui/slider';
import { openPinturaVideoModal } from '@/components/nodes/pintura/open';
import { useRouter } from 'next/navigation';
import {
  ConfirmFooter,
  getToolbarBtnCls,
  MoreToolPopover,
  SectionLabel,
  SegmentedRow,
  ToolbarActionButton,
  ToolListItem,
  TOOLBAR_POPOVER_CONTENT_CLS,
  TOOLBAR_SURFACE_CLS,
  TopToolbarShell,
  useCreateNeighborNode,
  useTopToolbarState,
} from '@/components/nodes/shared/top-toolbar-kit';
import SubtitleAreaDialog from '@/components/nodes/video/subtitle-area-dialog';
import { getDraftConvertState, useCreateFormalFromDraft } from '@/components/nodes/video/use-draft-to-formal';

// 顶部工具栏各功能接入的模型：通过模型配置 tags 解析，避免硬编码模型 ID。
// 模型注册表是静态的，模块级解析一次即可。
const UPSCALE_MODEL_IDS = getVideoModelIdsByTag(VIDEO_MODEL_TAGS.upscale);
const FPS_BOOST_MODEL_IDS = getVideoModelIdsByTag(VIDEO_MODEL_TAGS.fpsBoost);
const TRANSLATE_MODEL_IDS = getVideoModelIdsByTag(VIDEO_MODEL_TAGS.translate);
const WATERMARK_REMOVE_MODEL_IDS = getVideoModelIdsByTag(VIDEO_MODEL_TAGS.watermarkRemove);
const DESUBTITLE_MODEL_IDS = getVideoModelIdsByTag(VIDEO_MODEL_TAGS.desubtitle);

// 从 ui-options 注册表读取某个模型的 select 选项（单一来源），
// 如超分引擎（upscaler_model）、翻译目标语言（output_language）。
const getSelectUiOptions = (
  modelId: string | undefined,
  key: string,
): { label: string; value: string; default?: boolean }[] => {
  if (!modelId) return [];
  const opt = getUiOptionsForModel(modelId).find((o) => o.key === key);
  if (!opt || opt.type !== 'select') return [];
  const def = (opt as any).default as string | undefined;
  return (opt.options ?? []).map((o: any) => ({ label: String(o.label), value: String(o.value), default: o.value === def }));
};

type VideoModelToolPopoverProps = {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  triggerCls: string;
  contentCls: string;
  modelIds: string[];
  disabled: boolean;
  modelLabel: (id: string) => string;
  t: (key: string) => string;
  /** 额外参数：从 ui-options 注册表按 key 读取下拉选项（如翻译目标语言、擦除类型） */
  selects?: { optionKey: string; label: string }[];
  onConfirm: (modelId: string, opts: { selectValues?: Record<string, string> }) => void;
};

/**
 * 工具栏通用「选模型 -> （可选下拉参数）-> 确认」弹层，与图像顶部工具栏保持一致的样式。
 * 模型列表来自 tag 解析；无可用模型时整个按钮隐藏。
 */
function VideoModelToolPopover({
  icon: Icon,
  label,
  triggerCls,
  contentCls,
  modelIds,
  disabled,
  modelLabel,
  t,
  selects,
  onConfirm,
}: VideoModelToolPopoverProps) {
  const [modelId, setModelId] = useState<string | undefined>(modelIds[0]);
  // selects 是父组件每次渲染新建的数组字面量，不能直接作 memo/effect 依赖，
  // 否则父组件任意重渲染都会重置用户已选值；用 optionKey 拼接的稳定 key 代替。
  const selectsKey = (selects ?? []).map((s) => s.optionKey).join('|');
  const optionsByKey = useMemo(() => {
    const map: Record<string, { label: string; value: string; default?: boolean }[]> = {};
    for (const key of selectsKey ? selectsKey.split('|') : []) {
      map[key] = getSelectUiOptions(modelId, key);
    }
    return map;
  }, [modelId, selectsKey]);
  const visibleSelects = (selects ?? []).filter(
    (spec) => (optionsByKey[spec.optionKey] ?? []).length > 0,
  );
  const [selectValues, setSelectValues] = useState<Record<string, string>>({});
  // 切换模型时重置为该模型的默认选项
  useEffect(() => {
    const defaults: Record<string, string> = {};
    for (const [key, options] of Object.entries(optionsByKey)) {
      const def = options.find((o) => o.default)?.value ?? options[0]?.value;
      if (def) defaults[key] = def;
    }
    setSelectValues(defaults);
  }, [optionsByKey]);

  if (modelIds.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <ToolbarActionButton icon={Icon} label={label} className={triggerCls} disabled={disabled} />
      </PopoverTrigger>
      <PopoverContent align="start" className={contentCls}>
        <SectionLabel>{t('Choose model')}</SectionLabel>
        <div className="mb-2 flex flex-col gap-1">
          {modelIds.map((mid) => (
            <ToolListItem key={mid} selected={modelId === mid} onClick={() => setModelId(mid)}>
              {modelLabel(mid)}
            </ToolListItem>
          ))}
        </div>
        {visibleSelects.map((spec) => (
          <div key={spec.optionKey}>
            <SectionLabel>{spec.label}</SectionLabel>
            <div className="mb-2">
              <Select
                value={selectValues[spec.optionKey]}
                onValueChange={(v) => setSelectValues((prev) => ({ ...prev, [spec.optionKey]: v }))}
              >
                <SelectTrigger className="h-9 w-full rounded-lg border-transparent bg-sidebar-accent/30 hover:bg-sidebar-accent text-xs font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="fm-toolbar-surface z-[300] max-h-[280px] bg-sidebar/95 backdrop-blur-xl border border-sidebar-border rounded-xl shadow-none">
                  {(optionsByKey[spec.optionKey] ?? []).map((o) => (
                    <SelectItem key={o.value} value={o.value} className="text-xs">
                      {t(o.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
        <ConfirmFooter
          disabled={
            disabled || !modelId || visibleSelects.some((spec) => !selectValues[spec.optionKey])
          }
          onConfirm={() => {
            if (!modelId) return;
            onConfirm(modelId, { selectValues });
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

type VideoTopToolbarProps = {
  nodeId: string;
  videoUrl?: string;
  data: any;
  isToolbarBlocked: boolean;
};

export function VideoTopToolbar({
  nodeId,
  videoUrl,
  data,
  isToolbarBlocked,
}: VideoTopToolbarProps) {
  const { t, i18n } = useI18n();
  const isZh = (i18n?.language || '').toLowerCase().startsWith('zh');
  const router = useRouter();
  const { updateNodeData } = useReactFlow();
  const { getToken } = useAuth();
  const { user } = useUser();
  const project = useProject();
  const { extractToImageNode, extractToImageNodeAt } = useExtractFrame();

  const {
    isSelected,
    isMultiSelect,
    shown: topToolbarShown,
    pointerEnabled: topToolbarPointerEnabled,
  } = useTopToolbarState(nodeId, Boolean(videoUrl));

  // Local states
  const [isEditing, setIsEditing] = useState(false);
  // Extract-at slider
  const [showFrameSlider, setShowFrameSlider] = useState(false);
  const durationSec = useMemo(() => {
    const d1 = Number((data as any)?.durationSeconds);
    if (Number.isFinite(d1) && d1 > 0) return d1;
    const d2 = Number((data as any)?.duration);
    if (Number.isFinite(d2) && d2 > 0) return d2;
    const d3 = Number((data as any)?.generated?.durationSeconds);
    if (Number.isFinite(d3) && d3 > 0) return d3;
    // 更保守的回退值，避免误判 60s
    return 6;
  }, [data]);
  const [frameSec, setFrameSec] = useState<number>(0);
  useEffect(() => {
    if (!isSelected) setShowFrameSlider(false);
  }, [isSelected]);
  // Allow external trigger (e.g., context menu) to open slider
  useEffect(() => {
    const onOpen = (e: any) => {
      try {
        if (e?.detail?.id !== nodeId) return;
        setShowFrameSlider(true);
        try {
          window.dispatchEvent(new CustomEvent('video_pause', { detail: { id: nodeId } }));
        } catch {}
        const hint = Number(e?.detail?.seconds);
        if (Number.isFinite(hint) && hint >= 0) setFrameSec(Math.min(durationSec, hint));
      } catch {}
    };
    window.addEventListener('open_video_frame_slider', onOpen as any);
    return () => window.removeEventListener('open_video_frame_slider', onOpen as any);
  }, [nodeId, durationSec]);
  // 画质优化：节点模型来自 tag 解析，具体超分引擎选项来自 ui-options 注册表
  const upscaleNodeId = UPSCALE_MODEL_IDS[0];
  const upscaleSubModels = useMemo(() => getSelectUiOptions(upscaleNodeId, 'upscaler_model'), [upscaleNodeId]);
  const [enhanceModelId, setEnhanceModelId] = useState<string | undefined>(
    () => upscaleSubModels.find((o) => o.default)?.value ?? upscaleSubModels[0]?.value
  );
  const enhanceQualities = upscaleNodeId
    ? ((videoModels as any)?.[upscaleNodeId]?.qualities as string[] | undefined)
    : undefined;
  const [targetQuality, setTargetQuality] = useState<string>(() => (enhanceQualities?.[0] ?? '720p'));
  // 抽帧加载状态
  const [extractingFirst, setExtractingFirst] = useState(false);
  const [extractingLast, setExtractingLast] = useState(false);
  const [extractingAt, setExtractingAt] = useState(false);
  // 字幕擦除手动选区：弹层确认后暂存所选模型与参数，待框选完成再建节点
  const [desubtitleManual, setDesubtitleManual] = useState<{
    modelId: string;
    selectValues: Record<string, string>;
  } | null>(null);
  // 模型展示名：数据库 model_access.labels，未配置时回退到模型 ID
  const getLabelById = useModelLabelById();
  const modelLabel = useCallback(
    (mid: string) => getLabelById(mid) ?? mid,
    [getLabelById],
  );

  const createNeighborNode = useCreateNeighborNode(nodeId);
  const createFormalFromDraft = useCreateFormalFromDraft(nodeId);
  const draftConvert = getDraftConvertState(data);

  // Edit video
  const handleEditVideo = async () => {
    if (!videoUrl) return;
    try {
      setIsEditing(true);
      const edited = await openPinturaVideoModal({
        src: videoUrl,
        fps: 30,
        mimeType: 'video/webm',
      });
      if (!edited) return;
      const token = await getToken({ template: 'supabase' });
      if (!token || !user?.id) {
        try { toast.error(t('Please sign in to upload')); } catch {}
        return;
      }
      const inferredExt = edited.file.name.split('.').pop();
      const ext = inferredExt || ((edited.file.type || '').includes('mp4') ? 'mp4' : 'webm');
      const finalType = edited.file.type || 'video/webm';
      const file = new File([edited.file], `video-edit-${Date.now()}.${ext}`, { type: finalType });
      const uploaded = await uploadFile(file, 'files', {
        token,
        userId: user.id,
        projectId: String(project?.id || ''),
        assetType: 'video',
      });
      // Update node directly
      try {
        const patch: any = {
          content: { url: uploaded.url, type: uploaded.type },
          sourceUrl: uploaded.url,
          updatedAt: new Date().toISOString(),
        };
        if (Number.isFinite(edited.width) && Number.isFinite(edited.height) && (edited.width as number) > 0 && (edited.height as number) > 0) {
          // 真实分辨率只写 width/height；不写 aspectRatio（其仅承载比例设置）。
          patch.width = edited.width;
          patch.height = edited.height;
        }
        if (Number.isFinite(edited.durationSeconds) && (edited.durationSeconds as number) > 0) {
          patch.durationSeconds = edited.durationSeconds;
        }
        updateNodeData(nodeId, patch);
      } catch {}
      try { toast.success(t('Video updated')); } catch {}
    } catch (e) {
      handleError(t('Failed to edit video'), e);
    } finally {
      setIsEditing(false);
    }
  };

  if (!videoUrl || isMultiSelect) return null;

  // 隐藏时不构建弹层 JSX 树，避免画布上每个节点的无谓渲染开销
  const toolbarButtons = !topToolbarShown ? null : (
    <div className={TOOLBAR_SURFACE_CLS}>
      {(() => {
        const btnCls = getToolbarBtnCls(isZh);
        const popoverContentCls = TOOLBAR_POPOVER_CONTENT_CLS;
        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              {/* 画质优化 */}
              {upscaleNodeId && upscaleSubModels.length > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <ToolbarActionButton
                      icon={Sparkles}
                      label={t('Video upscale')}
                      className={btnCls}
                      disabled={isToolbarBlocked}
                    />
                  </PopoverTrigger>
                  <PopoverContent align="start" className={popoverContentCls}>
                    <SectionLabel>{t('Choose model')}</SectionLabel>
                    <div className="mb-2 flex flex-col gap-1">
                      {upscaleSubModels.map(({ value: mid, label }) => (
                        <ToolListItem key={mid} selected={enhanceModelId === mid} onClick={() => setEnhanceModelId(mid)}>
                          {label}
                        </ToolListItem>
                      ))}
                    </div>
                    <SectionLabel>{t('Choose quality')}</SectionLabel>
                    <SegmentedRow
                      options={enhanceQualities ?? ['720p', '1080p', '2k', '4k']}
                      value={targetQuality}
                      onChange={setTargetQuality}
                    />
                    <ConfirmFooter
                      disabled={isToolbarBlocked || !enhanceModelId}
                      onConfirm={() => createNeighborNode('video', upscaleNodeId, { quality: targetQuality, modelUi: { upscaler_model: enhanceModelId } })}
                    />
                  </PopoverContent>
                </Popover>
              )}
              {/* 视频补帧 */}
              <VideoModelToolPopover
                icon={Gauge}
                label={t('Video frame interpolation')}
                triggerCls={btnCls}
                contentCls={popoverContentCls}
                modelIds={FPS_BOOST_MODEL_IDS}
                disabled={isToolbarBlocked}
                modelLabel={modelLabel}
                t={t}
                onConfirm={(mid) => createNeighborNode('video', mid)}
              />
              {/* 视频翻译 */}
              <VideoModelToolPopover
                icon={Languages}
                label={t('Video translation')}
                triggerCls={btnCls}
                contentCls={popoverContentCls}
                modelIds={TRANSLATE_MODEL_IDS}
                disabled={isToolbarBlocked}
                modelLabel={modelLabel}
                t={t}
                selects={[{ optionKey: 'output_language', label: t('Target Language') }]}
                onConfirm={(mid, { selectValues }) =>
                  createNeighborNode('video', mid, selectValues?.output_language ? { modelUi: { output_language: selectValues.output_language } } : undefined)
                }
              />
              {/* 水印消除 */}
              <VideoModelToolPopover
                icon={Eraser}
                label={t('Watermark removal')}
                triggerCls={btnCls}
                contentCls={popoverContentCls}
                modelIds={WATERMARK_REMOVE_MODEL_IDS}
                disabled={isToolbarBlocked}
                modelLabel={modelLabel}
                t={t}
                onConfirm={(mid) => createNeighborNode('video', mid)}
              />
              {/* 字幕擦除 */}
              <VideoModelToolPopover
                icon={CaptionsOff}
                label={t('Subtitle removal')}
                triggerCls={btnCls}
                contentCls={popoverContentCls}
                modelIds={DESUBTITLE_MODEL_IDS}
                disabled={isToolbarBlocked}
                modelLabel={modelLabel}
                t={t}
                selects={[
                  { optionKey: 'subtitle_type', label: t('Erase type') },
                  { optionKey: 'desubtitle_model', label: t('Model version') },
                ]}
                onConfirm={(mid, { selectValues }) => {
                  // 手动选区：先在首帧上框选区域，确认后再建节点
                  if (selectValues?.subtitle_type === 'manual') {
                    if (!videoUrl) {
                      toast.error(t('Video is not ready yet'));
                      return;
                    }
                    setDesubtitleManual({ modelId: mid, selectValues });
                    return;
                  }
                  createNeighborNode('video', mid, selectValues && Object.keys(selectValues).length > 0 ? { modelUi: selectValues } : undefined);
                }}
              />
              {/* 自选帧提取 */}
              <Popover>
                <PopoverTrigger asChild>
                  <ToolbarActionButton
                    icon={ImageIcon}
                    label={t('Extract frame at')}
                    className={btnCls}
                    disabled={isToolbarBlocked}
                  />
                </PopoverTrigger>
                <PopoverContent align="start" className={popoverContentCls}>
                  <div className="flex flex-col gap-1">
                    {([
                      {
                        key: 'first',
                        label: t('Extract first frame'),
                        active: extractingFirst,
                        onClick: async () => {
                          try {
                            setExtractingFirst(true);
                            await extractToImageNode(nodeId, 'first', String(videoUrl));
                          } finally {
                            setExtractingFirst(false);
                          }
                        },
                      },
                      {
                        key: 'last',
                        label: t('Extract last frame'),
                        active: extractingLast,
                        onClick: async () => {
                          try {
                            setExtractingLast(true);
                            await extractToImageNode(nodeId, 'last', String(videoUrl));
                          } finally {
                            setExtractingLast(false);
                          }
                        },
                      },
                      {
                        key: 'at',
                        label: t('Extract frame at'),
                        active: false,
                        onClick: () => {
                          setFrameSec(0);
                          setShowFrameSlider(true);
                          try {
                            window.dispatchEvent(new CustomEvent('video_pause', { detail: { id: nodeId } }));
                          } catch { }
                        },
                      },
                    ] as const).map(({ key, label, active, onClick }) => (
                      <ToolListItem
                        key={key}
                        selected={active}
                        loading={active}
                        disabled={isToolbarBlocked || active}
                        onClick={onClick}
                      >
                        {label}
                      </ToolListItem>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex items-center gap-1">
              {/* 预览 */}
              <ToolbarActionButton
                icon={Eye}
                label={t('Preview')}
                className={btnCls}
                disabled={isToolbarBlocked}
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent('open_video_preview', { detail: { id: nodeId } }));
                  } catch { }
                }}
              />
              {/* 视频编辑器 */}
              <ToolbarActionButton
                icon={Pencil}
                label={t('Edit Video')}
                className={btnCls}
                loading={isEditing}
                disabled={isToolbarBlocked || isEditing}
                onClick={handleEditVideo}
              />
              {/* 视频解析 */}
              <ToolbarActionButton
                icon={ScanSearch}
                label={t('Parse video')}
                className={btnCls}
                disabled={isToolbarBlocked}
                onClick={() => {
                  try {
                    window.open('/projects/video', '_blank', 'noopener,noreferrer');
                  } catch {
                    router.push('/projects/video');
                  }
                }}
              />
              {/* 更多 */}
              <MoreToolPopover nodeId={nodeId} url={videoUrl} disabled={isToolbarBlocked} triggerCls={btnCls} />
            </div>
          </div>
        );
      })()}
    </div>
  );

  return (
    <>
      <TopToolbarShell shown={topToolbarShown} pointerEnabled={topToolbarPointerEnabled}>
        {toolbarButtons}
      </TopToolbarShell>
      {/* 样片转正式：节点右侧、与视频顶部对齐，随顶部工具栏一起显隐 */}
      {draftConvert.convertible ? (
        <NodeToolbarRaw
          isVisible={topToolbarShown}
          position={Position.Right}
          align="start"
          offset={20}
          className={`z-[120] transition-opacity ${topToolbarPointerEnabled ? '' : 'pointer-events-none'}`}
        >
          <div className={TOOLBAR_SURFACE_CLS}>
            <ToolbarActionButton
              icon={Clapperboard}
              label={draftConvert.expired ? t('Draft expired') : t('Generate final video')}
              title={draftConvert.expired ? t('Drafts can be converted within 7 days') : t('Generate a 1080p final video from this draft')}
              className={cn(getToolbarBtnCls(isZh), 'w-auto')}
              disabled={isToolbarBlocked || draftConvert.expired}
              onClick={createFormalFromDraft}
            />
          </div>
        </NodeToolbarRaw>
      ) : null}
      {desubtitleManual && videoUrl ? (
        <SubtitleAreaDialog
          open
          onOpenChange={(v) => {
            if (!v) setDesubtitleManual(null);
          }}
          videoUrl={videoUrl}
          onConfirm={(areas) => {
            createNeighborNode('video', desubtitleManual.modelId, {
              modelUi: { ...desubtitleManual.selectValues, subtitle_areas: areas },
            });
            setDesubtitleManual(null);
          }}
        />
      ) : null}
      {showFrameSlider && isSelected ? (
        <div className="absolute left-1 right-1 bottom-1 z-[115]">
          <div className="fm-toolbar-surface mx-auto max-w-[560px] rounded-2xl bg-sidebar/95 backdrop-blur-xl border border-sidebar-border/30 px-4 py-3 shadow-2xl">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/50">
              {t('Extract frame at {{seconds}}s', { seconds: Math.max(0, Math.min(durationSec, frameSec)).toFixed(1) })}
            </div>
            <div className="flex items-center gap-4">
              <div className="w-full h-8 flex items-center">
                <Slider
                  min={0}
                  max={Math.max(1, durationSec)}
                  step={0.1}
                  value={[Math.max(0, Math.min(durationSec, frameSec))]}
                  onValueChange={(v) => {
                    const val = Array.isArray(v) ? Number(v[0]) : Number(v as any);
                    if (Number.isFinite(val)) {
                      setFrameSec(val);
                      try {
                        window.dispatchEvent(
                          new CustomEvent('video_seek_to', {
                            detail: { id: nodeId, seconds: Math.max(0, Math.min(durationSec, val)) },
                          })
                        );
                      } catch { }
                    }
                  }}
                />
              </div>
              <div className="shrink-0 text-xs font-mono tabular-nums w-[60px] text-right text-foreground/70 bg-sidebar-accent/50 px-2 py-1 rounded-lg">
                {Math.max(0, Math.min(durationSec, frameSec)).toFixed(1)}s
              </div>
            </div>
            <div className="mt-3 flex items-center justify-end gap-2 border-t border-sidebar-border/30 pt-3">
              <Button
                size="sm"
                variant="ghost"
                className="rounded-xl h-9 px-4 text-xs font-bold hover:bg-sidebar-accent"
                onClick={() => {
                  setShowFrameSlider(false);
                  try {
                    window.dispatchEvent(new CustomEvent('video_play', { detail: { id: nodeId } }));
                  } catch { }
                }}
              >
                {t('Cancel')}
              </Button>
              <Button
                size="sm"
                className="rounded-xl h-9 px-4 text-xs font-bold shadow-lg shadow-primary/20 transition-all active:scale-95"
                disabled={extractingAt}
                onClick={async () => {
                  try {
                    setExtractingAt(true);
                    const sec = Math.max(0, Math.min(durationSec, frameSec));
                    await extractToImageNodeAt(nodeId, sec, String(videoUrl));
                    setShowFrameSlider(false);
                    try {
                      window.dispatchEvent(new CustomEvent('video_play', { detail: { id: nodeId } }));
                    } catch { }
                  } catch { }
                  finally {
                    setExtractingAt(false);
                  }
                }}
              >
                <span className="inline-flex items-center gap-1">
                  {extractingAt ? <Loader2Icon size={14} className="animate-spin" /> : null}
                  <span>{t('Extract current frame')}</span>
                </span>
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}


